import { useMemo } from 'react';
import { useKpiBugDetectionDays } from './useKpi.js';
import { extractDateString } from '../lib/date-utils.js';
import type { BugDetail, BugDetailParams } from '../types/api.js';

/** 1 日のミリ秒数。 @graph-connects none */
const MS_PER_DAY = 86_400_000;
/**
 * 分布チャート用に取得する最大バグ件数（BugDetailParams.limit の上限に合わせる）。
 * @graph-connects none
 */
const DISTRIBUTION_LIMIT = 500;

/** バグ検知日数分布チャートの 1 スポット（1 バグ）。 */
export interface BugDetectionPoint {
  /** 発見日（初回発生日優先・空なら起票日）。yyyy-mm-dd。 */
  date: string;
  /** 検知日数 = 発見日 − 起因案件リリース完了日（> 0 のみ）。 */
  detectionDays: number;
  /** バグチケットキー。 */
  issueKey: string;
}

/**
 * バグ詳細一覧を「検知日数 > 0 の個別スポット」へ変換する。
 * 発見日は初回発生日、空なら起票日。検知日数は発見日と起因案件リリース完了日の日数差で、
 * 0 以下（同日・逆転）は分布に出さないため除外する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ詳細一覧を発見日・検知日数・チケットキーの個別スポット配列へ変換し、検知日数0以下を除外する
 * @graph-connects extractDateString [calls] 日付文字列正規化
 */
export function toBugDetectionPoints(bugs: BugDetail[]): BugDetectionPoint[] {
  const points: BugDetectionPoint[] = [];
  for (const bug of bugs) {
    const discovery = extractDateString(bug.firstOccurrenceDate ?? bug.createdDate);
    const released = bug.causeProjectReleasedDate
      ? extractDateString(bug.causeProjectReleasedDate)
      : '';
    if (!discovery || !released) continue;
    const detectionDays = Math.round((Date.parse(discovery) - Date.parse(released)) / MS_PER_DAY);
    if (!Number.isFinite(detectionDays) || detectionDays <= 0) continue;
    points.push({ date: discovery, detectionDays, issueKey: bug.issueKey });
  }
  return points;
}

/**
 * バグ検知日数分布チャート用のデータフック。トレンドと同じ窓（直近 days 日）の案件起因バグを
 * 全件取得し、検知日数 > 0 の個別スポット配列に変換して返す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 指定チーム・直近days日の案件起因バグ明細を取得し、検知日数>0の個別スポット配列に変換して分布チャートへ供給する
 * @graph-connects useKpiBugDetectionDays [calls] バグ検知日数詳細データ取得
 * @graph-connects toBugDetectionPoints [calls] バグ明細→個別スポット変換
 */
export function useBugDetectionDistribution(
  team: BugDetailParams['team'],
  days: number,
): BugDetectionPoint[] {
  // 直近 days 日の窓（end=今日）をレンダーごとに算出する。useMemo([days]) で固定すると
  // 大画面に張りっぱなし等でページに長時間滞在したまま日付をまたいでも end が前進せず窓が古びる。
  // 同日内は yyyy-mm-dd 文字列が不変なので useKpiBugDetectionDays の queryKey は安定し、
  // 無駄なリフェッチは起きない（日付が変わった瞬間だけ新しい窓で再取得される）。
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days);

  const { data } = useKpiBugDetectionDays({
    team,
    startDate: fmt(start),
    endDate: fmt(end),
    limit: DISTRIBUTION_LIMIT,
  });

  return useMemo(() => toBugDetectionPoints(data?.data ?? []), [data]);
}
